/**
 * PublicProfile - Public user profile page (Instagram-style)
 *
 * Shows a user's public profile information by their publicName (alias).
 * Features:
 * - Avatar (initials, DiceBear, or custom)
 * - Bio and social media links
 * - Rankings across all challenge types
 * - Best scores and levels
 * - Member since (month/year only)
 * - Total challenges completed
 *
 * PRIVACY: Only shows publicName - never real name or email
 */
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import rankingsService from '../services/rankingsService'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorBoundary from '../components/ErrorBoundary'
import { Avatar, PublicScoreDisplay, BadgeShowcase } from '../components/profile'
import { ActivityHeatmap } from '../components/heatmap'
import {
  Trophy,
  Calendar,
  Target,
  TrendingUp,
  ArrowLeft,
  UserX,
  WifiOff,
  ServerCrash,
  RefreshCw,
  Instagram,
  MessageCircle,
  ExternalLink,
  Edit,
  GraduationCap,
  Flame,
  Video,
  CalendarDays,
  Shield,
  MessageSquare
} from 'lucide-react'
import { createLogger } from '../utils/logger'

const logger = createLogger('PublicProfile')

// TikTok icon (lucide doesn't have one)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
)

// X (formerly Twitter) icon
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
)

// Discord icon
const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
)

// Social platform configuration
const SOCIAL_ICONS = {
  instagram: Instagram,
  tiktok: TikTokIcon,
  snapchat: MessageCircle,
  twitter: XIcon,
  discord: DiscordIcon
}

const SOCIAL_URLS = {
  instagram: (handle) => `https://instagram.com/${handle}`,
  tiktok: (handle) => `https://tiktok.com/@${handle}`,
  snapchat: (handle) => `https://snapchat.com/add/${handle}`,
  twitter: (handle) => `https://x.com/${handle}`,
  discord: null // No direct link
}

const PublicProfile = () => {
  const { publicName, strategyType: highlightedStrategy } = useParams()
  const { user } = useAuthStore()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Strategy types for public profile display
  const strategyTypes = [
    { type: 'SAT_MATH', name: 'SAT Math', icon: '🧮' },
    { type: 'SAT_MATH_MINI', name: 'SAT Math Mini', icon: '⚡' },
    { type: 'SAT_READING_WRITING', name: 'SAT R&W', icon: '📖' }
  ]

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await rankingsService.getPublicProfile(publicName)
        setProfile(data)
      } catch (err) {
        logger.error('Error fetching profile:', err)
        if (err.status === 404) {
          // Server returns 404 for both not found and hidden profiles
          // to prevent user enumeration
          setError('notFound')
        } else if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
          setError('network')
        } else if (err.status >= 500) {
          setError('server')
        } else {
          setError('error')
        }
      } finally {
        setLoading(false)
      }
    }

    if (publicName) {
      fetchProfile()
    }
  }, [publicName])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error === 'notFound') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center max-w-md">
          <UserX className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Profile Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The user "{publicName}" doesn't exist or hasn't completed any challenges yet.
          </p>
          <Link to="/rankings" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            View Rankings
          </Link>
        </div>
      </div>
    )
  }

  if (error === 'network') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mx-auto mb-4">
            <WifiOff className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Connection Error
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Unable to connect to the server. Please check your internet connection and try again.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="btn-primary inline-flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
            <Link
              to="/rankings"
              className="btn-secondary inline-flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              View Rankings
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (error === 'server') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mx-auto mb-4">
            <ServerCrash className="w-8 h-8 text-orange-600 dark:text-orange-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Server Error</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Our servers are experiencing issues. Please try again in a few minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="btn-primary inline-flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
            <Link
              to="/rankings"
              className="btn-secondary inline-flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              View Rankings
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <UserX className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Error Loading Profile
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Something went wrong while loading this profile. Please try again.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="btn-primary inline-flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
            <Link
              to="/rankings"
              className="btn-secondary inline-flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              View Rankings
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const isOwnProfile = user?.publicName === publicName

  // Get highest level across all strategies
  const getHighestLevel = () => {
    if (!profile.levels?.length) return 1
    return Math.max(...profile.levels.map((l) => l.level || 1))
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Instagram-style Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back link */}
          <Link
            to="/rankings"
            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Rankings
          </Link>

          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <Avatar
                type={profile.avatar?.type}
                seed={profile.avatar?.seed}
                style={profile.avatar?.style}
                data={profile.avatar?.data}
                initials={profile.avatar?.initials}
                publicName={profile.publicName}
                size="xl"
                className="ring-4 ring-gray-100 dark:ring-gray-700"
              />
            </div>

            {/* Profile Info */}
            <div className="flex-1 text-center md:text-left">
              {/* Name, Coach Badge, and Edit button */}
              <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-light text-gray-900 dark:text-gray-100">
                    {profile.publicName}
                  </h1>
                  {profile.isCoach && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 border border-amber-200 dark:border-amber-700 rounded-full text-sm font-medium text-amber-700 dark:text-amber-400">
                      <Shield className="w-3.5 h-3.5" />
                      Coach
                    </span>
                  )}
                </div>
                {isOwnProfile && (
                  <Link
                    to="/profile#social"
                    className="px-4 py-1.5 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Profile
                  </Link>
                )}
              </div>

              {/* Stats Row - Instagram style */}
              <div className="flex justify-center md:justify-start gap-8 mb-4">
                <div className="text-center">
                  <span className="block font-bold text-gray-900 dark:text-gray-100">
                    {profile.totalChallenges || 0}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">challenges</span>
                </div>
                <div className="text-center">
                  <span className="block font-bold text-gray-900 dark:text-gray-100">
                    {profile.rankings?.filter((r) => r.rank).length || 0}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">rankings</span>
                </div>
                <div className="text-center">
                  <span className="block font-bold text-gray-900 dark:text-gray-100">
                    {getHighestLevel()}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">max level</span>
                </div>
              </div>

              {/* Bio */}
              {profile.bio && (
                <p className="text-gray-900 dark:text-gray-100 mb-4 whitespace-pre-line">
                  {profile.bio}
                </p>
              )}

              {/* Social Links */}
              {profile.socialHandles && Object.keys(profile.socialHandles).length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
                  {Object.entries(profile.socialHandles).map(([platform, handle]) => {
                    const Icon = SOCIAL_ICONS[platform]
                    const url = SOCIAL_URLS[platform]?.(handle)

                    if (!Icon || !handle) return null

                    return url ? (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-full text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        <Icon className="w-4 h-4" />
                        <span>@{handle}</span>
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </a>
                    ) : (
                      <span
                        key={platform}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-full text-sm"
                      >
                        <Icon className="w-4 h-4" />
                        <span>{handle}</span>
                      </span>
                    )
                  })}
                </div>
              )}

              {/* Coach Contact Links */}
              {profile.isCoach &&
                profile.coachInfo &&
                (profile.coachInfo.zoomLink ||
                  profile.coachInfo.calendlyLink ||
                  profile.coachInfo.contactNote) && (
                  <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageSquare className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                        Contact Coach
                      </span>
                    </div>

                    {profile.coachInfo.contactNote && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                        {profile.coachInfo.contactNote}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {profile.coachInfo.zoomLink && (
                        <a
                          href={profile.coachInfo.zoomLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          <Video className="w-4 h-4" />
                          Zoom
                          <ExternalLink className="w-3 h-3 opacity-70" />
                        </a>
                      )}
                      {profile.coachInfo.calendlyLink && (
                        <a
                          href={profile.coachInfo.calendlyLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          <CalendarDays className="w-4 h-4" />
                          Schedule
                          <ExternalLink className="w-3 h-3 opacity-70" />
                        </a>
                      )}
                    </div>
                  </div>
                )}

              {/* School */}
              {profile.school && (
                <div className="text-sm text-gray-600 dark:text-gray-300 flex flex-col sm:flex-row items-center justify-center md:justify-start gap-1 sm:gap-1.5 mb-2">
                  <div className="flex items-center gap-1.5">
                    <GraduationCap className="w-4 h-4 text-primary-500 flex-shrink-0" />
                    <span className="text-center sm:text-left">{profile.school.name}</span>
                  </div>
                  {(profile.school.city || profile.school.state) && (
                    <span className="text-gray-400 dark:text-gray-500">
                      {[profile.school.city, profile.school.state].filter(Boolean).join(', ')}
                    </span>
                  )}
                </div>
              )}

              {/* Member Since */}
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center md:justify-start gap-1">
                <Calendar className="w-4 h-4" />
                Member since {profile.memberSince}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* SAT Scores Section */}
        <div className="card">
          <div className="card-header flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Target className="w-5 h-5 text-primary-500" />
              SAT Score Progress
            </h2>
            {/* Streak Display */}
            {profile.streak && profile.streak.currentStreak > 0 && (
              <div className="flex items-center gap-1 text-orange-500">
                <Flame className="w-4 h-4" />
                <span className="font-bold text-sm">{profile.streak.currentStreak} day streak</span>
              </div>
            )}
          </div>
          <div className="card-body">
            <PublicScoreDisplay scores={profile.scores} goal={null} showGoalProgress={false} />
          </div>
        </div>

        {/* Badges Showcase */}
        <BadgeShowcase badges={profile.badges} totalBadges={profile.totalBadges} maxDisplay={6} />

        {/* Activity Heatmap */}
        {profile.activity && (
          <ActivityHeatmap
            activities={profile.activity.activities}
            stats={profile.activity.stats}
            currentStreak={profile.streak?.currentStreak}
            className=""
          />
        )}

        {/* Rankings by Challenge Type */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Rankings by Challenge Type
            </h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {strategyTypes.map((strategy) => {
                const ranking = profile.rankings?.find((r) => r.strategyType === strategy.type)
                const isHighlighted = highlightedStrategy === strategy.type
                return (
                  <RankingCard
                    key={strategy.type}
                    strategy={strategy}
                    ranking={ranking}
                    isHighlighted={isHighlighted}
                  />
                )
              })}
            </div>
          </div>
        </div>

        {/* Strategy Levels */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Current Levels
            </h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {strategyTypes.map((strategy) => {
                const level = profile.levels?.find((l) => l.strategyType === strategy.type)
                return (
                  <div
                    key={strategy.type}
                    className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="text-2xl mb-2">{strategy.icon}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      {strategy.name}
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      Lv {level?.level || 1}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* CTA for anonymous users */}
        {!user && (
          <div className="bg-gradient-to-r from-primary-500 to-purple-600 rounded-2xl p-8 text-center text-white">
            <Trophy className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h3 className="text-xl font-bold mb-2">Join the SAT Prep Community</h3>
            <p className="text-white/80 mb-6 max-w-md mx-auto">
              Track your progress, compete on leaderboards, and improve your scores.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/signup"
                className="px-6 py-2.5 bg-white text-primary-600 font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                Get Started
              </Link>
              <Link
                to="/login"
                className="px-6 py-2.5 bg-white/20 hover:bg-white/30 text-white font-medium rounded-lg transition-colors"
              >
                Log In
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Ranking Card - Shows ranking for a specific strategy type
 */
const RankingCard = ({ strategy, ranking, isHighlighted = false }) => {
  const hasRanking = ranking && ranking.rank

  return (
    <div
      className={`p-4 rounded-lg transition-all ${
        isHighlighted
          ? 'bg-gradient-to-br from-primary-50 to-purple-50 dark:from-primary-900/30 dark:to-purple-900/30 ring-2 ring-primary-500 dark:ring-primary-400 shadow-lg'
          : 'bg-gray-50 dark:bg-gray-800'
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">{strategy.icon}</span>
        <span className="font-medium text-gray-900 dark:text-gray-100">{strategy.name}</span>
        {isHighlighted && (
          <span className="ml-auto text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/50 px-2 py-0.5 rounded-full">
            Shared
          </span>
        )}
      </div>

      {hasRanking ? (
        <>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              #{ranking.rank}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              of {ranking.totalParticipants?.toLocaleString() || '?'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-purple-600 rounded-full"
                style={{ width: `${ranking.percentile || 0}%` }}
              />
            </div>
            <span className={`text-sm font-medium ${getPercentileColor(ranking.percentile || 0)}`}>
              {formatPercentileDisplay(ranking.percentile || 0)}
            </span>
          </div>
        </>
      ) : (
        <div className="text-gray-500 dark:text-gray-400">
          <p className="text-sm">Not ranked yet</p>
        </div>
      )}
    </div>
  )
}

/**
 * Format percentile for display
 * "Top 0%" -> "Best" for clarity
 */
const formatPercentileDisplay = (percentile) => {
  const topPercent = 100 - percentile
  if (topPercent <= 0) return 'Best'
  if (topPercent < 1) return 'Top 1%'
  return `Top ${Math.round(topPercent)}%`
}

/**
 * Get color class for percentile display
 */
const getPercentileColor = (percentile) => {
  if (percentile >= 95) return 'text-purple-500'
  if (percentile >= 90) return 'text-blue-500'
  if (percentile >= 75) return 'text-green-500'
  if (percentile >= 50) return 'text-yellow-600'
  return 'text-gray-500'
}

// Wrap with ErrorBoundary for graceful error handling
const PublicProfileWithErrorBoundary = () => (
  <ErrorBoundary>
    <PublicProfile />
  </ErrorBoundary>
)

export default PublicProfileWithErrorBoundary
